import React, {
    useEffect, useContext, useState, useCallback, useMemo
} from 'react';
import { useHistory } from 'react-router-dom';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { RenewalService } from 'gw-capability-renewal';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import {
    ADOBE_TOPICS,
    SgCepChanges,
    SgActiveTestsContext,
    SgSessionStorageService,
    SgDocumentsUtil,
    useSgTrackPageName
} from 'saga-modules';
import _ from 'lodash';
import {
    SgRenewalPremium, SgSPHRenewalSummaryInfo, SgSPMRenewalSummaryInfo
} from 'sg-renewal';
import SgWebchatInfo from 'sg-webchat-info';
import SgRenewalUpgradeAccordion from 'sg-renewal-upgrade-accordion';
import SgCepSideMenu from 'sg-cep-side-menu';
import { CepConstants } from 'saga-cep-specific-components';
import { Loader, Link } from '@jutro/components';

import CommonConstants from 'sg-common-constants';
import { publish } from '@jutro/events';
import { TranslatorContext } from '@jutro/locale';
import SgCepLobSpecificUtil from 'sg-cep-lob-specific-util';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage } from 'gw-portals-wizard-react';
import SgSptRenewalSummaryInfo from 'sg-spt-renewal-summary-info';
import SgSptRenewalPremium from 'sg-renewal/sg-spt-renewal-premium/SgSptRenewalPremium';
import metadata from './SgRenewalSummaryPage.metadata.json5';
import metadataWOC3143 from './SgRenewalSummaryPage_WOC3143.metadata.json5';
import messages from './SgRenewalSummaryPage.messages';

function SgRenewalSummaryPage(props) {
    const {
        wizardData: modelVM,
        updateWizardData: updateData,
        jumpTo,
        steps,
        goNext
    } = props;
    const translator = useContext(TranslatorContext);
    const history = useHistory();
    const { activeMvtTests } = useContext(SgActiveTestsContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const contactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_RENEWAL_COVER_LEVELS_PHONE_NUMBER;
    const contactUsPhoneNumber2 = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_MOTOR_PHONE_NUMBER2;

    const persistentModel = SgSessionStorageService.getItem(SgSessionStorageService.KEY.MODEL);
    const jobNumber = _.get(modelVM.value, 'jobID')
        || _.get(modelVM.value, 'policyNumber')
        || _.get(persistentModel, 'jobID')
        || _.get(persistentModel, 'policyNumber');
    const [isUpgrading, setIsUpgrading] = useState(_.get(modelVM.value, 'isUpgrading') || _.get(persistentModel, 'isUpgrading'));
    const [isLoading, setLoadingState] = useState(true);

    const viewModelService = useContext(ViewModelServiceContext);

    useSgTrackPageName(CepConstants.PAGE_NAMES.RENEWAL_PROPOSAL);

    const renewalPromise = useCallback(() => {
        if (isUpgrading) {
            return RenewalService.upgradeRenewalCoverLevel(jobNumber);
        }
        return RenewalService.getRenewalProposalDetails(jobNumber);
    }, [isUpgrading, jobNumber]);

    useEffect(() => {
        if (!viewModelService) {
            return;
        }
        renewalPromise().then((renewalDTO) => {
            RenewalService.loadRenewal(jobNumber, renewalDTO.branchNumber)
                .then((renewal) => {
                    const newModelVM = viewModelService.clone(modelVM);
                    newModelVM.value = renewal;
                    newModelVM.value.isUpgrading = isUpgrading;
                    SgCepChanges.savePreviousModel(_.cloneDeep(newModelVM.value));
                    updateData(newModelVM);
                    SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, newModelVM.value);
                    publish(ADOBE_TOPICS.FILTER_JOURNEY_PRODUCT, {
                        renewal: newModelVM
                    });
                })
                .finally(() => {
                    setLoadingState(false);
                });
        });
    }, [viewModelService, isUpgrading]);

    const updateDataAndJumpTo = useCallback(async (step, renewalDetails) => {
        const renewalVM = _.clone(modelVM);
        _.merge(renewalVM.value.renewalDetails, renewalDetails);
        updateData(renewalVM);
        goNext();
    }, [modelVM, updateData, goNext]);

    const continueRenewal = useCallback(() => {
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.CEP_RENEWAL_ADDONS_PAGE_AVAILABLE);
        const declarationPageIndex = _.findIndex(
            steps,
            ({ path }) => path === '/declarations'
        );
        jumpTo(declarationPageIndex);
    }, [steps, jumpTo]);

    const continueToAddons = useCallback(() => {
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.CEP_RENEWAL_ADDONS_PAGE_AVAILABLE, true);
        updateDataAndJumpTo('RenewalAddons', modelVM.value.renewalDetails);
    }, [modelVM, updateDataAndJumpTo]);

    const backToOriginalOffer = useCallback(() => {
        setIsUpgrading(false);
        setLoadingState(true);
    }, []);

    const goHome = useCallback(() => {
        history.push('/home');
    }, [history]);

    const comprehensiveCoverBannerContent = useMemo(() => {
        if (modelVM.value.renewalDetails) {
            return (
                <p id="confirmRenewalWordingBanner">
                    {translator(messages[`${modelVM.value.renewalDetails.productCode}ComprehensiveCoverLabel_WOC3143`], {
                        phoneNumber1: isMobile
                            ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                            : <b>{contactUsPhoneNumber}</b>,
                        phoneNumber2: isMobile
                            ? <a href={`tel:${contactUsPhoneNumber2.replaceAll(' ', '')}`}>{contactUsPhoneNumber2}</a>
                            : <b>{contactUsPhoneNumber2}</b>
                    })}
                </p>
            );
        }
        return null;
    }, [modelVM, isMobile, contactUsPhoneNumber, contactUsPhoneNumber2, translator]);

    const renewalDocumentsContainerContent = useMemo(() => {
        if (modelVM.value.renewalDetails) {
            return (
                <p id="confirmRenewalWording">
                    {translator(messages.ConfirmRenewalLabel_WOC3143)}
                    {(modelVM.value.renewalDetails.renewalDocument) && (
                        <Link
                            href="/"
                            id="downloadRenewalDocuments"
                            onClick={(event) => SgDocumentsUtil.downloadDocument(event,
                                modelVM.value.renewalDetails.renewalDocument.policyNumber, modelVM.value.renewalDetails.renewalDocument.sagaType)}
                        >
                            {translator(messages.RenewalDocumentLabel_WOC3143)}
                        </Link>
                    )}
                </p>
            );
        }
        return null;
    }, [modelVM, translator]);

    const cepRenewalInfoMobileContent = useMemo(() => {
        if (modelVM.value.renewalDetails) {
            return modelVM.value.renewalDetails.termNumber <= 3
                ? (
                    <p id="shopAroundLessThanThree">{translator(messages.ShopAroundLessThanThreeLabel_WOC3143)}</p>
                ) : (
                    <p id="shopAroundMoreThanFour">{translator(messages.ShopAroundMoreThanFourLabel_WOC3143)}</p>
                );
        }
        return null;
    }, [modelVM, translator]);

    function getPhoneNumber() {
        switch (modelVM.value.renewalDetails.productCode) {
            case CommonConstants.MOTOR_PRODUCT_CODE:
                return CepConstants.MOTOR_CHEQUE_CONTACT_US_NUMBER;
            case CommonConstants.HOME_PRODUCT_CODE:
                return CepConstants.HOME_CHEQUE_CONTACT_US_NUMBER;
            default:
                return CepConstants.HOME_CHEQUE_CONTACT_US_NUMBER;
        }
    }

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrideProps = {
        spmSummaryInfo: {
            visible: modelVM.baseData.productCode.value === CommonConstants.MOTOR_PRODUCT_CODE,
            renewalDetails: modelVM.value.renewalDetails,
            updateDataAndJumpTo
        },
        sphSummaryInfo: {
            visible: modelVM.baseData.productCode.value === CommonConstants.HOME_PRODUCT_CODE,
            renewalDetails: modelVM.value.renewalDetails
        },
        sptSummaryInfo: {
            visible: modelVM.baseData.productCode.value === CommonConstants.TRAVEL_PRODUCT_CODE,
            modelVM: modelVM
        },
        cepRenewalPremium: {
            renewalDetails: modelVM.value.renewalDetails,
            visible: modelVM.baseData.productCode.value !== CommonConstants.TRAVEL_PRODUCT_CODE,
        },
        cepSptRenewalPremium: {
            visible: modelVM.baseData.productCode.value === CommonConstants.TRAVEL_PRODUCT_CODE,
            renewalDetails: modelVM.renewalDetails.value
        },
        cepRenewalInfoMobile: {
            content: cepRenewalInfoMobileContent
        },
        cepSummaryUpgradeContainer: {
            visible: modelVM.value.renewalDetails.canUpgradeCoverLevel
                || (isUpgrading === true && SgCepLobSpecificUtil.showUpgradedRenewalBanner(modelVM)),
        },
        renewWithoutChanges: {
            visible: !modelVM.value.renewalDetails.isCashChequePayment,
            content: SgCepLobSpecificUtil.renewalSummaryContinueButtonLabel(modelVM, activeMvtTests, translator),
            onClick: (modelVM.baseData.productCode.value === CommonConstants.MOTOR_PRODUCT_CODE
                && activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_1837)) ? continueToAddons : continueRenewal
        },
        cashChequePaymentContainer: {
            visible: modelVM.value.renewalDetails.isCashChequePayment
        },
        cashChequePayment2: {
            content: translator(messages.CashCheque2, { number: getPhoneNumber() })
        },
        summaryUpgradeInfo: {
            renewal: modelVM.value.renewalDetails,
            isUpgrading,
            backToOriginalOffer,
            showDivider: false
        },
        backToMainPage: {
            visible: !isUpgrading
        },
        backToOriginalOffer: {
            visible: isUpgrading === true
        },
        comprehensiveCoverBanner: {
            content: comprehensiveCoverBannerContent
        },
        renewalDocumentsContainer: {
            visible: !modelVM.value.renewalDetails.isCashChequePayment,
            content: renewalDocumentsContainerContent
        },
        byClickingContinueWording: {
            visible: !modelVM.value.renewalDetails.isCashChequePayment
        },
        cepRenewalPremiumWebchatMessage: {
            visible: modelVM.baseData.productCode.value === CommonConstants.HOME_PRODUCT_CODE
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            continueRenewal,
            continueToAddons,
            backToOriginalOffer,
            goHome
        },
        resolveComponentMap: {
            SgSPMRenewalSummaryInfo,
            SgSPHRenewalSummaryInfo,
            SgSptRenewalSummaryInfo,
            SgRenewalPremium,
            SgSptRenewalPremium,
            SgRenewalUpgradeAccordion,
            SgWebchatInfo,
            SgCepSideMenu
        }
    };

    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(SgCepLobSpecificUtil.canShowWOC3143Summary(modelVM, activeMvtTests)
                ? metadataWOC3143.pageContent : metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

export default SgRenewalSummaryPage;
